import { Vector3, Web3DViewer } from '@technology/web3d';
import { Point3D, ThreeJsManager, type PickResult } from '@trimble/utilities';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, createSignal } from 'solid-js';
import { StationsNavigationCapability } from './stations-navigation-capability';

const HEIGHT_ABOVE_MAP = 1;

/**
 * @intent 2D-map variant of {@link StationsNavigationCapability}. Renders
 * markers and paths on a fixed-height plane and exposes picked positions
 * via {@link pickedStationPosition} instead of mutating navigation state.
 */
export class MapStationCapability extends StationsNavigationCapability {
  private readonly pickedStationPositionSignal: Accessor<Point3D | undefined>;
  private readonly setPickedStationPosition: (
    position: Point3D | undefined
  ) => void;

  constructor(
    viewer: Web3DViewer,
    stationsProvider: StationsCapabilityProvider,
    threeJsManager: ThreeJsManager
  ) {
    super(viewer, stationsProvider, threeJsManager, undefined, {
      stationHeightOffset: 0,
      stationPointSize: 0.7,
      // Same multiplier near/far: map stations sit at a fixed height so
      // distance-based LOD doesn't apply.
      hoverScaleMultiplier: 2.0,
      hoverScaleMultiplierFar: 2.0,
    });
    const [pickedStationPosition, setPickedStationPosition] = createSignal<
      Point3D | undefined
    >(undefined, { equals: false });
    this.pickedStationPositionSignal = pickedStationPosition;
    this.setPickedStationPosition = setPickedStationPosition;
  }

  public get pickedStationPosition(): Accessor<Point3D | undefined> {
    return this.pickedStationPositionSignal;
  }

  protected override getStationZ(_stationPosition: Vector3): number {
    return HEIGHT_ABOVE_MAP;
  }

  /**
   * Marker-only pick: paths in 2D map view aren't clickable, so this
   * skips the parent's path-segment fall-through.
   */
  protected override readonly handlePick = (result: PickResult): void => {
    const point = this.viewManager.findMarkerByMeshId(result.meshId);
    this.setPickedStationPosition(point?.originalPosition);
  };

  /** Markers stay visible whenever the capability is enabled (no `isActive` gate). */
  protected override shouldShowStationMarkers(): boolean {
    return this.isEnabled();
  }

  public override trackIsEnabled(): void {
    // No-op override: the base implementation disables the capability when
    // the provider has no items, which would also disable picking — we
    // need picking to remain available even on an empty list.
  }
}
